/**
 * Stylised "stack of PDF report pages" mock — three overlapping cards
 * rotated to suggest a freshly-printed report. The top card shows a
 * miniature of the actual audit cover page (donut + section pills).
 *
 * Pure CSS, no images required.
 */
export default function PDFStack({ className = "", variant = "light" }) {
  const isDark = variant === "dark";
  const surface = isDark ? "bg-white" : "bg-white";

  const sub = [
    { label: "SEO",  v: 73, color: "#1D4ED8" },
    { label: "GEO",  v: 38, color: "#0891B2" },
    { label: "AEO",  v: 44, color: "#9333EA" },
    { label: "PERF", v: 62, color: "#0F766E" },
    { label: "SEC",  v: 55, color: "#B91C1C" },
    { label: "A11Y", v: 57, color: "#6D28D9" },
  ];

  return (
    <div className={`relative ${className}`} data-testid="pdf-stack">
      {/* Back card */}
      <div className={`absolute inset-0 ${surface} border border-border rounded-sm shadow-xl translate-x-6 translate-y-6 -rotate-3 opacity-90`} />
      {/* Middle card */}
      <div className={`absolute inset-0 ${surface} border border-border rounded-sm shadow-xl translate-x-3 translate-y-3 rotate-2 opacity-95`} />

      {/* Top card — actual content */}
      <div className={`relative ${surface} border border-ink rounded-sm shadow-2xl p-6 flex flex-col h-full`}>
        {/* Tiny header strip */}
        <div className="flex items-center justify-between pb-3 border-b border-border mb-4">
          <img src="/logo-light.png" alt="Auditor" className="h-5 w-auto" />
          <span className="text-[8px] mono tracking-widest text-ink-muted">TECHNICAL WEBSITE AUDIT</span>
        </div>

        <div className="text-[9px] mono tracking-wider text-ink-muted mb-1">AUDIT REPORT</div>
        <div className="font-display text-2xl font-black tracking-tight leading-none mb-4 truncate">
          fasterbetter.ca
        </div>

        {/* Donut + verdict */}
        <div className="flex items-center gap-4 mb-4">
          <Donut score={59} size={84} color="#E85D04" />
          <div>
            <div className="text-[9px] mono tracking-wider text-ink-muted">OVERALL</div>
            <div className="font-display text-xl font-black text-warning leading-tight">Needs work</div>
            <div className="text-[10px] text-ink-muted mono">HTTP 200 · 2,212 ms</div>
          </div>
        </div>

        {/* Sub-scores */}
        <div className="grid grid-cols-6 gap-1.5">
          {sub.map((s) => (
            <div key={s.label} className="border border-border rounded-sm py-2 px-1 flex flex-col items-center">
              <Donut score={s.v} size={28} color={s.color} stroke={3.5} hideNumber />
              <div className="num-display text-xs font-black mt-1" style={{ color: s.color }}>{s.v}</div>
              <div className="text-[7px] mono text-ink-muted tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Bottom hint of paragraphs */}
        <div className="mt-auto pt-4 space-y-1.5">
          {[0.85, 0.95, 0.7].map((w, i) => (
            <div key={i} className="h-1.5 rounded-full bg-secondary" style={{ width: `${w * 100}%` }} />
          ))}
        </div>

        {/* Tiny footer strip */}
        <div className="flex items-center justify-between mt-4 pt-2 border-t border-border text-[8px] mono text-ink-muted">
          <span>1.844.606.1100  ·  hello@auditmysite.ca</span>
          <span>Page 1</span>
        </div>
      </div>
    </div>
  );
}

function Donut({ score, size = 60, color = "#002FA7", stroke = 8, hideNumber = false }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#E9ECEF" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      {!hideNumber && (
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="Cabinet Grotesk, sans-serif"
          fontWeight="800"
          fontSize={size * 0.36}
          fill={color}
        >
          {score}
        </text>
      )}
    </svg>
  );
}
